import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Result } from 'antd';
import { ImgWrapper, ScreenViewerWrapper } from './styles';
import { type ScreenshotEvent } from '@src/pages/VisualActivation/store';
import ClickableElement from '@src/pages/VisualActivation/RoomDetail/WorkSpace/ScreenViewer/ClickableElement';

const ScreenViewer = ({
  screenshotData,
  allMappedEvents,
  selectEvent,
  onCreateEvent,
}: {
  screenshotData: ScreenshotEvent[];
  latestEvent?: any;
  allMappedEvents: any[];
  selectEvent: any;
  onCreateEvent: (v: any) => void;
}) => {
  const [scaleFactor, setScaleFactor] = useState(1);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { elementsData, screenshot } = screenshotData[0] || {};

  const clickableElements = elementsData?.clickableElements || [];
  const [originalWidth = 0, originalHeight = 0] = (elementsData?.displaySize || '')
    .replace(/[{}]/g, '')
    .split(',')
    .map(Number);

  // Calculate scale factor when image loads or container size changes
  useEffect(() => {
    const calculateScale = () => {
      if (!originalWidth || !originalHeight) {
        return;
      }

      // ScreenViewerWrapper has fixed width of 700px with 10px padding on each side
      const maxWidth = 700 - 20; // 680px available width
      const maxHeight = window.innerHeight - 300; // Account for header, footer, and other UI elements

      const scaleX = maxWidth / originalWidth;
      const scaleY = maxHeight / originalHeight;
      const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      setScaleFactor(scale);
    };

    calculateScale();

    const handleResize = () => calculateScale();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [originalWidth, originalHeight, screenshot]);

  const highlightElement = useMemo(() => {
    if (!selectEvent?.fullEventAttributes) {
      return undefined;
    }

    return clickableElements.find((element: any) => {
      return Object.entries(selectEvent.fullEventAttributes)
        .filter(([key]) => key !== 'state') // state is not stable, so we don't use it to highlight the element
        .every(([key, value]) => element[key] === value);
    });
  }, [clickableElements, selectEvent]);

  return (
    <div ref={containerRef}>
      <ScreenViewerWrapper>
        {screenshot ? (
          <ImgWrapper>
            <img
              ref={imgRef}
              style={{
                width: originalWidth * scaleFactor || 'initial',
                height: originalHeight * scaleFactor || 'auto',
              }}
              src={screenshot}
              alt="screenshot"
            />
            {clickableElements.map((element: any) => (
              <ClickableElement
                key={element.frame}
                element={element}
                onClick={onCreateEvent}
                allMappedEvents={allMappedEvents}
                scaleFactor={scaleFactor}
              />
            ))}
            {/* TODO: not just button click, just check deos the event has frame, if has frame, then highlight the element */}
            {highlightElement && (
              <ClickableElement
                highlight
                element={highlightElement}
                onClick={onCreateEvent}
                allMappedEvents={allMappedEvents}
                scaleFactor={scaleFactor}
              />
            )}
          </ImgWrapper>
        ) : (
          <Result status="warning" title="No screenshot available" />
        )}
      </ScreenViewerWrapper>
    </div>
  );
};

export default ScreenViewer;
